import React, { useEffect, useRef } from "react";

const ParticleBackground = ({ type = "fireflies", enabled = true }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!enabled || type === "none") return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    let animationFrameId;
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    // Initialize particles depending on type
    const particles = [];
    const particleCount = type === "rain" ? 100 : type === "snow" ? 70 : type === "fireflies" ? 35 : 45;

    class Particle {
      constructor() {
        this.reset(true);
      }

      reset(init = false) {
        this.x = Math.random() * canvas.width;
        this.y = init ? Math.random() * canvas.height : (type === "rain" ? -20 : -10);
        this.size = Math.random() * 2 + 1;
        this.speedX = type === "rain" ? -0.7 : type === "snow" ? Math.random() * 0.8 - 0.4 : Math.random() * 0.5 - 0.25;
        this.speedY = type === "rain" ? Math.random() * 5 + 7 : type === "snow" ? Math.random() * 1.2 + 0.8 : Math.random() * 0.4 - 0.15;
        this.opacity = Math.random() * 0.5 + 0.25;
        this.opacitySpeed = Math.random() * 0.015 + 0.005;
        this.color = "";
        this.angle = Math.random() * 360;
        this.spin = Math.random() * 1.6 - 0.8;

        if (type === "snow") {
          this.size = Math.random() * 2.8 + 1.2;
          this.color = `rgba(245, 240, 235, ${this.opacity})`;
        } else if (type === "rain") {
          this.size = Math.random() * 14 + 10; // length of rain drop
          this.color = "rgba(174, 219, 237, 0.35)";
        } else if (type === "fireflies") {
          this.color = `rgba(235, 194, 91, ${this.opacity})`;
          this.size = Math.random() * 2.5 + 1.5;
        } else if (type === "dust") {
          this.color = `rgba(213, 199, 183, ${this.opacity})`;
          this.size = Math.random() * 1.8 + 0.8;
        } else if (type === "leaves") {
          this.size = Math.random() * 5 + 3.5;
          const leafColors = ["rgba(224, 122, 95, 0.45)", "rgba(214, 167, 106, 0.45)", "rgba(129, 178, 154, 0.45)"];
          this.color = leafColors[Math.floor(Math.random() * leafColors.length)];
          this.speedX = Math.random() * 0.8 - 1.4; // Drift left
          this.speedY = Math.random() * 0.6 + 0.9; // Fall down
        }
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        
        if (type === "fireflies") {
          this.opacity += this.opacitySpeed;
          if (this.opacity > 0.85 || this.opacity < 0.15) {
            this.opacitySpeed = -this.opacitySpeed;
          }
          this.color = `rgba(235, 194, 91, ${this.opacity})`;
        }

        if (type === "leaves") {
          this.angle += this.spin;
        }

        // Reset if off-screen or faded completely
        if (this.y > canvas.height + 20 || this.x < -20 || this.x > canvas.width + 20) {
          this.reset(false);
        }
      }

      draw() {
        ctx.beginPath();
        if (type === "rain") {
          ctx.strokeStyle = this.color;
          ctx.lineWidth = 1;
          ctx.moveTo(this.x, this.y);
          ctx.lineTo(this.x + this.speedX, this.y + this.size);
          ctx.stroke();
        } else if (type === "leaves") {
          ctx.fillStyle = this.color;
          ctx.save();
          ctx.translate(this.x, this.y);
          ctx.rotate((this.angle * Math.PI) / 180);
          ctx.ellipse(0, 0, this.size, this.size / 2.2, 0, 0, 2 * Math.PI);
          ctx.fill();
          ctx.restore();
        } else if (type === "fireflies") {
          ctx.fillStyle = this.color;
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillStyle = this.color;
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    // Populate
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.update();
        p.draw();
      });
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, [type, enabled]);

  if (!enabled || type === "none") return null;

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-0 pointer-events-none w-full h-full"
    />
  );
};

export default ParticleBackground;
